import React, {FC, useEffect, useState} from "react";
import { Editor as TinyEditor } from "@tinymce/tinymce-react";
import Error from "../Error";
import "./editor.scss";
import AppLoader from "../AppLoader";

interface EditorProps {
    value: string;
    onChangeHandler: (content: string) => void;
    error?: string;
    readOnly?: boolean;
    /** While AI streams the report in, use muted “skeleton” text; clears to normal when false. */
    aiReportStreaming?: boolean;
    editorInstanceRef: React.MutableRefObject<any>;
}

const STREAMING_BODY_CLASS = "ai-report-streaming";

const Editor: FC<EditorProps> = ({
                                     value,
                                     onChangeHandler,
                                     error,
                                     readOnly = false,
                                     aiReportStreaming = false,
                                     editorInstanceRef,
                                 }) => {
    const [editorLoading, setEditorLoading] = useState(true);

    useEffect(() => {
        const ed = editorInstanceRef.current;
        if (!ed || editorLoading || typeof ed.mode?.set !== "function") {
            return;
        }
        ed.mode.set(readOnly ? "readonly" : "design");
    }, [readOnly, editorLoading, editorInstanceRef]);

    useEffect(() => {
        const ed = editorInstanceRef.current;
        if (!ed || editorLoading) return;
        const body = ed.getBody?.();
        if (!body) return;
        if (aiReportStreaming) {
            body.classList.add(STREAMING_BODY_CLASS);
        } else {
            body.classList.remove(STREAMING_BODY_CLASS);
        }
    }, [aiReportStreaming, editorLoading, editorInstanceRef]);

    const convertWordFakeLists = (root: HTMLElement) => {
        const paragraphs = Array.from(root.querySelectorAll("p"));
        let currentList: HTMLUListElement | HTMLOListElement | null = null;

        const BULLET_REGEX = /^[\s\u00A0]*[•·▪◦■\-o][\s\u00A0]*/;
        const NUMBER_REGEX = /^[\s\u00A0]*\d+\.[\s\u00A0]*/;

        paragraphs.forEach(p => {
            const text = p.textContent || "";

            const isBullet = BULLET_REGEX.test(text);
            const isNumber = NUMBER_REGEX.test(text);

            if (!isBullet && !isNumber) {
                currentList = null;
                return;
            }

            // Create list if needed
            if (
                !currentList ||
                (isBullet && currentList.tagName !== "UL") ||
                (isNumber && currentList.tagName !== "OL")
            ) {
                currentList = document.createElement(isBullet ? "ul" : "ol");
                p.parentNode?.insertBefore(currentList, p);
            }

            const li = document.createElement("li");
            li.innerHTML = p.innerHTML;

            removeLeadingWordBullet(li, isNumber ? NUMBER_REGEX : BULLET_REGEX);

            currentList.appendChild(li);
            p.remove();
        });
    };

    const removeLeadingWordBullet = (
        li: HTMLElement,
        regex: RegExp
    ) => {
        const spans = Array.from(li.querySelectorAll("span"));

        for (const span of spans) {
            const text = span.textContent?.trim() || "";

            const style = span.getAttribute("style") || "";
            const isWordBulletSpan =
                regex.test(text) ||
                /symbol|wingdings|webdings/i.test(style);

            if (isWordBulletSpan) {
                span.remove();
                break;
            }
        }

        const walker = document.createTreeWalker(li, NodeFilter.SHOW_TEXT);
        const firstTextNode = walker.nextNode();

        if (firstTextNode && regex.test(firstTextNode.nodeValue || "")) {
            firstTextNode.nodeValue =
                firstTextNode.nodeValue?.replace(regex, "") || "";
        }
    };

    return (
        <div className="editor">
            {editorLoading && (
                <div className="editor-loader">
                    <AppLoader loading />
                </div>
            )}
            <TinyEditor
                tinymceScriptSrc="/tinymce/tinymce.min.js"
                licenseKey="gpl"
                value={value}
                onInit={(_, editor) => {
                    editorInstanceRef.current = editor;
                    setEditorLoading(false);
                    if (readOnly && typeof editor.mode?.set === "function") {
                        editor.mode.set("readonly");
                    }
                    editor.on("PastePostProcess", (e) => {
                        const node = e.node as HTMLElement;
                        if (!node) return;

                        const html = node.innerHTML;

                        // Detect Word paste
                        const isWord =
                            /mso-|urn:schemas-microsoft-com:office|class="?Mso/i.test(html);

                        if (!isWord) return;

                        convertWordFakeLists(node);
                    });

                    // Register an autocompleter
                    // editor.ui.registry.addAutocompleter('my-autocomplete', {
                    //     trigger: '@',
                    //     minChars: 1,
                    //     columns: 1,
                    //     fetch: (pattern, maxResults, fetchOptions) => {
                    //         const suggestions = ['Hepatic Cyst', 'Hepatic Fibrosis', 'Portal Vein Thrombosis', 'Chronic Cholecystitis'];
                    //         const results = suggestions
                    //             .filter(name => name.toLowerCase().startsWith(pattern.toLowerCase()))
                    //             .slice(0, maxResults)
                    //             .map(name => ({ value: name, text: name }));
                    //         return Promise.resolve(results); // wrap in Promise
                    //     },
                    //     onAction: (autocompleteApi, rng, value) => {
                    //         const editor = editorInstanceRef.current;
                    //         editor.selection.setRng(rng);
                    //         editor.insertContent(value);
                    //         autocompleteApi.hide();
                    //     }
                    // });
                }}
                onEditorChange={(content) => {
                    onChangeHandler(content);
                }}
                init={{
                    height: 700,
                    menubar: "file edit insert format table",
                    branding: false,
                    promotion: false,
                    plugins: ["advlist","lists","table","image","pagebreak","paste","link"],
                    toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link media table mergetags | image addcomment showcomments | spellcheckdialog a11ycheck typography uploadcare | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
                    file_picker_types: "image",
                    file_picker_callback: (callback) => {
                        const input = document.createElement("input");
                        input.type = "file";
                        input.accept = "image/*";
                        input.onchange = () => {
                            // @ts-ignore
                            const file = input.files[0];
                            const reader = new FileReader();
                            reader.onload = () => {
                                callback(reader.result as string, { title: file.name });
                            };
                            reader.readAsDataURL(file);
                        };
                        input.click();
                    },
                    paste_as_text: false,
                    paste_enable_default_filters: true,
                    paste_merge_formats: true,

                    content_style: `
                      body {
                        font-family: Arial, sans-serif;
                        font-size: 14px;
                        line-height: 1.4;
                      }

                      ul, ol {
                        padding-left: 24px;
                        margin: 0 0 8px 0;
                      }

                      li {
                        line-height: 1.4;
                        margin: 0;
                      }

                      p {
                        margin: 0 0 8px 0;
                        line-height: 1.4;
                      }

                      /* Muted “skeleton” look while AI streams; removed when generation finishes */
                      body.${STREAMING_BODY_CLASS} {
                        color: #94a3b8;
                      }
                      body.${STREAMING_BODY_CLASS} * {
                        color: inherit !important;
                      }
                      body.${STREAMING_BODY_CLASS} table,
                      body.${STREAMING_BODY_CLASS} th,
                      body.${STREAMING_BODY_CLASS} td {
                        border-color: #cbd5e1 !important;
                      }
                    `,
                }}
            />

            <Error message={error ?? ""} />
        </div>
    );
};

export default Editor;